import './styles/index.css';

import React, {lazy, Suspense} from 'react';
import {Route, BrowserRouter as Router, Switch} from 'react-router-dom';

import {Experiment, Variant} from '@marvelapp/react-ab-test';
import {LocalizationHandler} from './i18n/LocalizationHandler';
import {screenConfigs as emailConfirmationConfig} from './projects/emailConfirmation-0424/constants';
import {
  accountRecoveryConfig,
  fitbitOAuthConfig,
  garminOAuthConfig,
} from './screens/UniversalLinkFallback/fallbackConfigs';
import {customVariant, originalVariant, webOnboardingV3Routing} from '.';
import Analytics from './utils/Analytics';
import LoadingScreen from './screens/LoadingScreen';
import {GRADUATION_ARMS} from './claude-generated-paywalls/graduation-signaling/arms';
import {getSearchParams} from './utils/urlQuery';
import LinkOutGate from './components/LinkOutGate';
import {RequireToken} from './components/RequireToken';
import {RouteErrorBoundary} from './components/RouteErrorBoundary';
import StripeLoadFallback from './components/StripeLoadFallback';

// Highest-traffic paywalls (Amplitude `[Web Payments] Paywall Viewed`, Aug–Oct 2026: 8k–89k
// users each, vs. <500 for everything else) and the screens they navigate to after purchase.
// These stay in the main bundle so the hot path never waits on a second chunk.
import EmailConfirmationCheckoutScreen from './projects/emailConfirmation-0424/screens/EmailConfirmationCheckoutScreen';
import SocialProofBelowFoldScreen from './claude-generated-paywalls/I36-social-proof-below-fold/SocialProofBelowFoldScreen';
import EmailConfirmationTrialScreen from './projects/emailConfirmation-0424/screens/EmailConfirmationTrialScreen';
import TrialCancelPaywall from './projects/trialCancelPaywall/TrialCancelPaywall';
import TrialCancelSuccess from './projects/trialCancelPaywall/TrialCancelSuccess';
import NameGreetingScreen from './claude-generated-paywalls/I03-name-personalization/screens/NameGreetingScreen';
import TrialFramedPlansScreen from './claude-generated-paywalls/I63-plan-label-framing/screens/TrialFramedPlansScreen';
import GraduationPaywallScreen from './claude-generated-paywalls/graduation-signaling/screens/GraduationPaywallScreen';
import GraduationTrialScreen from './claude-generated-paywalls/graduation-signaling/screens/GraduationTrialScreen';

// Everything else is code-split per route: visitors to the screens above don't download it.
// `lazyNamed` adapts a named export to React.lazy, which only takes default exports.
const lazyNamed = <K extends string, T extends Record<K, React.ComponentType<any>>>(
  factory: () => Promise<T>,
  name: K,
) => lazy(() => factory().then(module => ({default: module[name]})));

const OnboardingReversion3 = lazyNamed(() => import('./onboarding-v3/Page'), 'Page');
const DynamicOfferScreen = lazy(() => import('./screens/DynamicOfferScreen/DynamicOfferScreen'));
const DynamicSuccess = lazy(() => import('./screens/DynamicOfferScreen/DynamicSuccess'));
const PaymentComplete = lazy(() => import('./screens/DynamicOfferScreen/PaymentComplete'));
const KeywordGreeting = lazyNamed(() => import('./screens/KeywordGreeting'), 'KeywordGreeting');
const PartnerConnectPage = lazy(() => import('./screens/PartnerConnect/PartnerConnectPage'));
const SignUpPage = lazy(() => import('./screens/SignUpPage/SignUpPage'));
const SignUpSuccessPage = lazy(() => import('./screens/SignUpSuccess/SignUpSuccess'));
const StripeCustomSuccess = lazy(() => import('./screens/StripeCustomCheckout/StripeCustomSuccess'));
const StripeElementProvider = lazy(() => import('./screens/StripeCustomCheckout/StripeElementProvider'));
const ThankYou = lazy(() => import('./screens/ThankYou/ThankYou'));
const WebVsAppStorePrelude = lazy(() => import('./screens/WebVsAppStore/WebVsAppStorePrelude'));
const WebOnboardingV3Paywall = lazy(() => import('./onboarding-v3/paywall/WebOnboardingV3Paywall'));
const LifetimeOfferSuccess = lazy(() => import('./lifetime-offer/paywall/LifetimeOfferSuccess'));
const DesignTrialCheckoutScreen = lazy(
  () => import('./projects/emailConfirmation-0424/screens/DesignTrialCheckoutScreen'),
);
const GenderVideoCheckoutScreen = lazy(
  () => import('./projects/emailConfirmation-0424/screens/GenderVideoCheckoutScreen'),
);
const AiBundlingCheckoutScreen = lazy(
  () => import('./claude-generated-paywalls/ai-bundling/screens/AiBundlingCheckoutScreen'),
);
const AiBundlingSuccessScreen = lazy(
  () => import('./claude-generated-paywalls/ai-bundling/screens/AiBundlingSuccessScreen'),
);
const AiBundlingPaidTrialCheckoutScreen = lazy(
  () => import('./claude-generated-paywalls/ai-bundling/screens/AiBundlingPaidTrialCheckoutScreen'),
);
const AiBundleTrialChoiceScreen = lazy(
  () => import('./claude-generated-paywalls/ai-bundling/screens/AiBundleTrialChoiceScreen'),
);
const EmailConfirmationCheckoutWithBottomSheetScreen = lazy(
  () => import('./projects/emailConfirmation-0424/screens/EmailConfirmationCheckoutWithBottomSheetScreen'),
);
const SocialProofClosedDrawerScreen = lazy(
  () => import('./claude-generated-paywalls/social-proof/screens/SocialProofClosedDrawerScreen'),
);
const EmailConfirmationFeatureCardScreen = lazy(
  () => import('./projects/emailConfirmation-0424/screens/EmailConfirmationFeatureCardScreen'),
);
const StudentEmailConfirmationCheckoutScreen = lazy(
  () => import('./projects/emailConfirmation-0424/screens/StudentEmailConfirmationCheckoutScreen'),
);
const OpenInApp = lazy(() => import('./screens/OpenInApp/OpenInApp'));
const UniversalLinkFallback = lazy(() => import('./screens/UniversalLinkFallback/UniversalLinkFallback'));
const ChooseYourPriceTrialReminderScreen = lazy(
  () => import('./projects/emailConfirmation-0424/screens/ChooseYourPrice/ChooseYourPriceTrialReminderScreen'),
);
const ChooseYourPriceCheckoutScreen = lazy(
  () => import('./projects/emailConfirmation-0424/screens/ChooseYourPrice/ChooseYourPriceCheckoutScreen'),
);
const TwoWeekOfferScreen = lazy(() => import('./screens/TwoWeekOffer/TwoWeekOfferScreen'));
const StudentTwoWeekOfferScreen = lazyNamed(
  () => import('./screens/TwoWeekOffer/TwoWeekOfferScreen'),
  'StudentTwoWeekOfferScreen',
);
const FortyPercentOfferScreen = lazyNamed(
  () => import('./screens/FortyPercentOffer/FortyPercentOfferScreen'),
  'FortyPercentOfferScreen',
);
const FortyPercentStudentOfferScreen = lazyNamed(
  () => import('./screens/FortyPercentOffer/FortyPercentOfferScreen'),
  'FortyPercentStudentOfferScreen',
);
const AIRecapPaywall = lazy(() => import('./projects/aiRecap/AIRecapPaywall'));
const ExpiredPaywall = lazy(() => import('./projects/expiredPaywall/ExpiredPaywall'));
const ExpiredPaywallSuccessScreen = lazy(() => import('./projects/expiredPaywall/ExpiredPaywallSuccessScreen'));
const ResumeScreen = lazy(() => import('./projects/resume/ResumeScreen'));
const NativeWebPaywall = lazy(() => import('./projects/nativeWebPaywall/NativeWebPaywall'));
const EmailCampaignCheckoutScreen = lazy(
  () => import('./claude-generated-paywalls/email-campaign/EmailCampaignCheckoutScreen'),
);
const OutcomeTimelineSleepScreen = lazy(
  () => import('./claude-generated-paywalls/C5-link-out-design-test/screens/OutcomeTimelineSleepScreen'),
);
const OutcomeTimelineBedtimeScreen = lazy(
  () => import('./claude-generated-paywalls/C5-link-out-design-test/screens/OutcomeTimelineBedtimeScreen'),
);
const DataVizSleepScreen = lazy(
  () => import('./claude-generated-paywalls/C5-link-out-design-test/screens/DataVizSleepScreen'),
);
const DataVizBedtimeScreen = lazy(
  () => import('./claude-generated-paywalls/C5-link-out-design-test/screens/DataVizBedtimeScreen'),
);
const MonthlyCheckoutScreen = lazy(() => import('./claude-generated-paywalls/monthlyPaywall/MonthlyCheckoutScreen'));
const OutcomeTimelineSleepV2Screen = lazy(
  () => import('./claude-generated-paywalls/C5-link-out-design-test/screens/OutcomeTimelineSleepV2Screen'),
);
const EnergyFocusScreen = lazy(() => import('./claude-generated-paywalls/EF1-energy-focus/screens/EnergyFocusScreen'));
const Recommend099Screen = lazy(
  () => import('./claude-generated-paywalls/C6-plan-grid-iteration/screens/Recommend099Screen'),
);
const PerDayRateScreen = lazy(
  () => import('./claude-generated-paywalls/C6-plan-grid-iteration/screens/PerDayRateScreen'),
);
const RenewalTransparencyScreen = lazy(
  () => import('./claude-generated-paywalls/C6-plan-grid-iteration/screens/RenewalTransparencyScreen'),
);
const NameEmphasizedScreen = lazy(
  () => import('./claude-generated-paywalls/I03-name-personalization/screens/NameEmphasizedScreen'),
);
const NameHeroScreen = lazy(
  () => import('./claude-generated-paywalls/I03-name-personalization/screens/NameHeroScreen'),
);
const PersonalNoteScreen = lazy(
  () => import('./claude-generated-paywalls/I03-name-personalization/screens/PersonalNoteScreen'),
);
const GiftCheckoutScreen = lazy(() => import('./projects/gifting/screens/GiftCheckoutScreen'));
const GiftRedeemScreen = lazy(() => import('./projects/gifting/screens/GiftRedeemScreen'));
const GiftSuccessScreen = lazy(() => import('./projects/gifting/screens/GiftSuccessScreen'));
const SpecialOfferCheckoutScreen = lazy(() => import('./projects/trialCancelPaywall/SpecialOfferCheckoutScreen'));

export const Routes: React.FC = () => {
  return (
    <Router>
      <LinkOutGate>
        <LocalizationHandler>
          <RouteErrorBoundary>
            <Suspense fallback={<LoadingScreen />}>
              <Switch>
                <Route path="/thankyou">
                  <ThankYou />
                </Route>
                <Route path="/sign-up">
                  <SignUpPage />
                </Route>
                <Route path="/sign-up-success">
                  <SignUpSuccessPage />
                </Route>
                <Route path="/partner-connect">
                  <PartnerConnectPage />
                </Route>
                <Route path="/greetings">
                  <KeywordGreeting />
                </Route>
                <Route path="/intro-to-rise">
                  <Experiment name={webOnboardingV3Routing}>
                    <Variant name={originalVariant}>
                      <OnboardingReversion3 />
                    </Variant>
                    <Variant name={customVariant}>
                      <Route
                        path=""
                        component={() => {
                          window.location.href =
                            'https://apps.apple.com/us/app/rise-energy-sleep-tracker/id1453884781?ppid=719990e9-e0ee-408c-9811-1bb5fc41eebc';
                          return null;
                        }}
                      />
                    </Variant>
                  </Experiment>
                </Route>
                <Route path="/start-rise/:name" component={WebOnboardingV3Paywall} />
                <Route
                  path="/lifetime-offer/:name"
                  component={({match}: {match: {params: {name: string}}}) => {
                    Analytics.track('Lifetime Offer Redirect', {
                      from: window.location.pathname,
                      to: '/offer/rise',
                      code: match.params.name,
                    });
                    const params = getSearchParams();
                    params.set('expired', 'true');
                    window.location.href = `${window.location.origin}/offer/rise?${params.toString()}`;
                    return null;
                  }}
                />
                <Route path="/lifetime-offer-success" component={LifetimeOfferSuccess} />
                <Route path="/offer/success" component={DynamicSuccess} />
                <Route path="/offer/:name" component={DynamicOfferScreen} />
                <Route path="/payment-complete" component={PaymentComplete} />
                <Route path="/try-rise/:name" component={StripeElementProvider} />
                <Route path="/payment-success/" component={StripeCustomSuccess} />
                <Route path="/support-app-devs/:name" component={WebVsAppStorePrelude} />
                <Route path="/open-in-app">
                  <OpenInApp />
                </Route>
                <Route path="/account-recovery">
                  <UniversalLinkFallback config={accountRecoveryConfig} />
                </Route>
                <Route path="/fitbit-oauth">
                  <UniversalLinkFallback config={fitbitOAuthConfig} />
                </Route>
                <Route path="/garmin-oauth">
                  <UniversalLinkFallback config={garminOAuthConfig} />
                </Route>
                <Route path={emailConfirmationConfig.emailConfirmationFeatures.url}>
                  <EmailConfirmationFeatureCardScreen />
                </Route>
                <Route path={emailConfirmationConfig.emailConfirmationTrialConfirmation.url}>
                  <EmailConfirmationTrialScreen />
                </Route>
                <Route path="/insights-recap">
                  <AIRecapPaywall initialOption={'annual_70'} otherOption={'30d099_annual_70'} isStudent={false} />
                </Route>
                <Route path="/trial-cancel">
                  <RequireToken paywall={'trial-cancel'}>
                    <TrialCancelPaywall initialOption={'0d_annual_35'} otherOption={'monthly_10'} isStudent={false} />
                  </RequireToken>
                </Route>
                <Route path={'/expired'}>
                  <ExpiredPaywall initialOption={'0d_annual_70'} otherOption={'monthly_10'} isStudent={false} />
                </Route>
                <Route path={'/student-expired'}>
                  <ExpiredPaywall initialOption={'0d_annual_36'} otherOption={'monthly_10'} isStudent={true} />
                </Route>
                <Route path={'/expired-success'}>
                  <ExpiredPaywallSuccessScreen />
                </Route>
                {/* D11 one-tap reactivation for lapsed web subscribers. Opened by the app with a
              signed resume token (?rt=), which index.html scrubs from the URL on arrival. */}
                <Route exact path={'/resume'}>
                  <ResumeScreen />
                </Route>
                <Route path={'/update-success'}>
                  <TrialCancelSuccess />
                </Route>
                <Route path={emailConfirmationConfig.emailConfirmationCheckout.url}>
                  <RequireToken paywall={'email-confirmation-checkout'}>
                    <EmailConfirmationCheckoutScreen
                      initialOption={'annual_70'}
                      otherOption={'30d099_annual_70'}
                      isStudent={false}
                    />
                  </RequireToken>
                </Route>
                {/* Param-driven (reads ?gender=); default/fallback entry point. */}
                <Route exact path="/gender-video-checkout">
                  <GenderVideoCheckoutScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                {/* Dedicated URLs that force the video without a ?gender= param, so traffic can be
              split by URL (GrowthBook / email / Nyx link) without a Nyx release. */}
                <Route exact path="/gender-video-checkout-male">
                  <GenderVideoCheckoutScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                    forcedVariant={'male'}
                  />
                </Route>
                <Route exact path="/gender-video-checkout-female">
                  <GenderVideoCheckoutScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                    forcedVariant={'female'}
                  />
                </Route>
                {/* Student pricing (annual_36) mirrors of the three routes above. */}
                <Route exact path="/student-gender-video-checkout">
                  <GenderVideoCheckoutScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                  />
                </Route>
                <Route exact path="/student-gender-video-checkout-male">
                  <GenderVideoCheckoutScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                    forcedVariant={'male'}
                  />
                </Route>
                <Route exact path="/student-gender-video-checkout-female">
                  <GenderVideoCheckoutScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                    forcedVariant={'female'}
                  />
                </Route>
                <Route exact path="/ai-bundle-trial-choice">
                  <AiBundleTrialChoiceScreen isStudent={false} />
                </Route>
                <Route exact path="/ai-bundle-paid-trial">
                  <AiBundlingPaidTrialCheckoutScreen
                    initialOption={'30d099_ai_annual_90'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route exact path="/ai-bundle">
                  <RequireToken paywall={'ai-bundle-checkout'}>
                    <AiBundlingCheckoutScreen
                      initialOption={'ai_annual_90'}
                      otherOption={'annual_70'}
                      isStudent={false}
                    />
                  </RequireToken>
                </Route>
                <Route exact path="/student-ai-bundle">
                  <RequireToken paywall={'ai-bundle-checkout'}>
                    <AiBundlingCheckoutScreen
                      initialOption={'ai_annual_90'}
                      otherOption={'annual_36'}
                      isStudent={true}
                    />
                  </RequireToken>
                </Route>
                <Route path="/ai-bundle-success">
                  <AiBundlingSuccessScreen />
                </Route>
                <Route path="/email-confirmation-checkout-bottom-sheet">
                  <EmailConfirmationCheckoutWithBottomSheetScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route path="/student-email-confirmation-checkout-bottom-sheet">
                  <EmailConfirmationCheckoutWithBottomSheetScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                  />
                </Route>
                <Route path="/email-confirmation-checkout-bottom-sheet-open">
                  <EmailConfirmationCheckoutWithBottomSheetScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                    bottomSheetOpenByDefault={true}
                  />
                </Route>
                <Route path="/student-email-confirmation-checkout-bottom-sheet-open">
                  <EmailConfirmationCheckoutWithBottomSheetScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                    bottomSheetOpenByDefault={true}
                  />
                </Route>
                <Route path={emailConfirmationConfig.emailConfirmationCheckoutStudentPrices.url}>
                  {/* Same screen as the standard checkout, so the same guard: without a token it can't take payment. */}
                  <RequireToken paywall={'email-confirmation-checkout'}>
                    <StudentEmailConfirmationCheckoutScreen
                      initialOption={'annual_36'}
                      otherOption={'30d249_annual_36'}
                      isStudent={true}
                    />
                  </RequireToken>
                </Route>
                <Route path={emailConfirmationConfig.chooseYourPriceCheckout.url}>
                  <ChooseYourPriceCheckoutScreen />
                </Route>
                <Route path={emailConfirmationConfig.chooseYourPriceCheckoutTrialConfirmation.url}>
                  <ChooseYourPriceTrialReminderScreen />
                </Route>
                <Route path={emailConfirmationConfig.designTrialCheckout.url}>
                  <DesignTrialCheckoutScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route path={emailConfirmationConfig.designTrialStudentCheckout.url}>
                  <DesignTrialCheckoutScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                  />
                </Route>
                <Route path="/two-week-offer">
                  <RequireToken paywall={'two-week-offer'}>
                    <TwoWeekOfferScreen />
                  </RequireToken>
                </Route>
                <Route path="/student-two-week-offer">
                  <RequireToken paywall={'student-two-week-offer'}>
                    <StudentTwoWeekOfferScreen />
                  </RequireToken>
                </Route>
                <Route path="/forty-percent-off">
                  <RequireToken paywall={'forty-percent-off'}>
                    <FortyPercentOfferScreen />
                  </RequireToken>
                </Route>
                <Route path="/student-forty-percent-off">
                  <RequireToken paywall={'student-forty-percent-off'}>
                    <FortyPercentStudentOfferScreen />
                  </RequireToken>
                </Route>
                <Route path="/new-offer">
                  <NativeWebPaywall initialOption={'annual_70'} otherOption={'30d099_annual_70'} isStudent={false} />
                </Route>
                <Route path="/student-new-offer">
                  <NativeWebPaywall initialOption={'annual_36'} otherOption={'30d249_annual_36'} isStudent={true} />
                </Route>
                <Route path="/social-proof">
                  <SocialProofClosedDrawerScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route path="/student-social-proof">
                  <SocialProofClosedDrawerScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                  />
                </Route>
                <Route path="/dst26">
                  <EmailCampaignCheckoutScreen option={'annual_42'} />
                </Route>
                {/* C5: Link Out Paywall Design Test */}
                <Route exact path="/outcome-timeline-sleep">
                  <OutcomeTimelineSleepScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route exact path="/student-outcome-timeline-sleep">
                  <OutcomeTimelineSleepScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                  />
                </Route>
                <Route path="/outcome-timeline-sleep-v2">
                  <OutcomeTimelineSleepV2Screen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route path="/student-outcome-timeline-sleep-v2">
                  <OutcomeTimelineSleepV2Screen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                  />
                </Route>
                <Route path="/outcome-timeline-bedtime">
                  <OutcomeTimelineBedtimeScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route path="/student-outcome-timeline-bedtime">
                  <OutcomeTimelineBedtimeScreen
                    initialOption={'annual_36'}
                    otherOption={'30d249_annual_36'}
                    isStudent={true}
                  />
                </Route>
                <Route path="/data-viz-sleep">
                  <DataVizSleepScreen initialOption={'annual_70'} otherOption={'30d099_annual_70'} isStudent={false} />
                </Route>
                <Route path="/student-data-viz-sleep">
                  <DataVizSleepScreen initialOption={'annual_36'} otherOption={'30d249_annual_36'} isStudent={true} />
                </Route>
                <Route path="/data-viz-bedtime">
                  <DataVizBedtimeScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route path="/student-data-viz-bedtime">
                  <DataVizBedtimeScreen initialOption={'annual_36'} otherOption={'30d249_annual_36'} isStudent={true} />
                </Route>
                <Route path="/special-offer">
                  <SpecialOfferCheckoutScreen />
                </Route>
                <Route path="/monthly">
                  <MonthlyCheckoutScreen />
                </Route>
                {/* EF1: Energy Focus Paywall */}
                <Route exact path="/energy">
                  <EnergyFocusScreen initialOption={'annual_70'} otherOption={'30d099_annual_70'} isStudent={false} />
                </Route>
                <Route exact path="/student-energy">
                  <EnergyFocusScreen initialOption={'annual_36'} otherOption={'30d249_annual_36'} isStudent={true} />
                </Route>
                {/* C6: Plan Grid Iteration — link-out paywall, 3-arm test on the plan grid itself */}
                <Route exact path="/recommend-099">
                  <Recommend099Screen initialOption={'30d099_annual_70'} otherOption={'annual_70'} isStudent={false} />
                </Route>
                <Route exact path="/per-day-rate">
                  <PerDayRateScreen initialOption={'annual_70'} otherOption={'30d099_annual_70'} isStudent={false} />
                </Route>
                <Route exact path="/renewal-transparency">
                  <RenewalTransparencyScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                {/* I03: Name personalization on link-out paywall — 4-arm intensity ladder */}
                <Route exact path="/name-greeting">
                  <RequireToken paywall={'name-greeting'}>
                    <NameGreetingScreen
                      initialOption={'annual_70'}
                      otherOption={'30d099_annual_70'}
                      isStudent={false}
                    />
                  </RequireToken>
                </Route>
                <Route exact path="/name-emphasized">
                  <NameEmphasizedScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route exact path="/name-hero">
                  <NameHeroScreen initialOption={'annual_70'} otherOption={'30d099_annual_70'} isStudent={false} />
                </Route>
                <Route exact path="/personal-note">
                  <PersonalNoteScreen initialOption={'annual_70'} otherOption={'30d099_annual_70'} isStudent={false} />
                </Route>
                {/* I63: plan-card label framing — isolates the plan-card relabel that shipped to B10's
              experimental_control arm only. Forked from /name-greeting (the current production
              route for this cohort); the ONLY delta is the plan-card title/subtitle. */}
                <Route exact path="/trial-framed-plans">
                  <TrialFramedPlansScreen
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                {/* I36: social proof BELOW the plan grid. Both arms are /name-greeting (production for
              this cohort) with a credibility block appended under the fold — nothing above the
              CTA changes, and no step is added. This is the follow-up the April 2026
              /social-proof interstitial asked for after losing 7.7%/9.4% of trial starts to the
              friction of being an extra screen. Non-student only in round one: that test hurt
              students ~22% worse, so they get their own read later, not a forked route now. */}
                <Route exact path="/press-badges">
                  <SocialProofBelowFoldScreen
                    variant={'quiet'}
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                <Route exact path="/social-proof-scroll">
                  <SocialProofBelowFoldScreen
                    variant={'full'}
                    initialOption={'annual_70'}
                    otherOption={'30d099_annual_70'}
                    isStudent={false}
                  />
                </Route>
                {/* Graduation Signaling A/B test — "signaling" & "control" arms, duplicated from
              /name-greeting. Copy-only deltas (header above the grid, CTA, plan-card labels).
              Each arm navigates to its own trial-timeline route. */}
                <Route exact path="/graduation-signaling">
                  <RequireToken paywall={GRADUATION_ARMS.signaling.paywallSlug}>
                    <GraduationPaywallScreen
                      config={GRADUATION_ARMS.signaling}
                      initialOption={'annual_70'}
                      otherOption={'30d099_annual_70'}
                      isStudent={false}
                    />
                  </RequireToken>
                </Route>
                <Route exact path="/student-graduation-signaling">
                  <RequireToken paywall={GRADUATION_ARMS.signaling.paywallSlug}>
                    <GraduationPaywallScreen
                      config={GRADUATION_ARMS.signaling}
                      initialOption={'annual_36'}
                      otherOption={'30d249_annual_36'}
                      isStudent={true}
                    />
                  </RequireToken>
                </Route>
                <Route exact path="/graduation-control">
                  <RequireToken paywall={GRADUATION_ARMS.control.paywallSlug}>
                    <GraduationPaywallScreen
                      config={GRADUATION_ARMS.control}
                      initialOption={'annual_70'}
                      otherOption={'30d099_annual_70'}
                      isStudent={false}
                    />
                  </RequireToken>
                </Route>
                <Route exact path="/student-graduation-control">
                  <RequireToken paywall={GRADUATION_ARMS.control.paywallSlug}>
                    <GraduationPaywallScreen
                      config={GRADUATION_ARMS.control}
                      initialOption={'annual_36'}
                      otherOption={'30d249_annual_36'}
                      isStudent={true}
                    />
                  </RequireToken>
                </Route>
                <Route exact path="/graduation-signaling-trial">
                  <GraduationTrialScreen arm={'signaling'} paywall={GRADUATION_ARMS.signaling.paywallSlug} />
                </Route>
                <Route exact path="/graduation-control-trial">
                  <GraduationTrialScreen arm={'control'} paywall={GRADUATION_ARMS.control.paywallSlug} />
                </Route>
                {/* Gift Rise (D07 / RET-2709). One-time $69.99 for 12 months of Rise for someone
              else. /gift is the pitch + wallet button; /gift/success and /gift/redeem land
              in follow-up PRs. Must stay above the "*" catch-all below. */}
                <Route exact path="/gift">
                  <GiftCheckoutScreen />
                </Route>
                <Route exact path="/gift/success">
                  <GiftSuccessScreen />
                </Route>
                <Route exact path="/gift/redeem">
                  <GiftRedeemScreen />
                </Route>
                <Route
                  path="*"
                  component={() => {
                    const removedRoutes = [
                      '/redirect',
                      '/new-member-offer',
                      '/black-friday-2020',
                      '/black-friday-2021',
                      '/new-years-2021',
                      '/world-sleep-day-2021',
                      '/renewal-offer',
                      '/start-trial',
                      '/onboarding',
                      '/email-signup',
                      '/job-selection',
                      '/self-reported-weekday',
                      '/self-reported-free-day',
                      '/self-reported-confirmation',
                      '/sleep-need',
                      '/sleep-debt',
                      '/energy-profile',
                      '/web-onboarding-paywall',
                      '/web-onboarding-stripe-confirmation',
                    ];
                    const path = window.location.pathname;
                    const isRemovedRoute = removedRoutes.some(r => path.startsWith(r));
                    Analytics.track('Removed Route Visited', {
                      path,
                      search: window.location.search,
                      isLegacyHostedCheckout: isRemovedRoute,
                    });
                    if (isRemovedRoute) {
                      window.location.href = `${window.location.origin}/offer/rise`;
                    }
                    return null;
                  }}
                />
              </Switch>
            </Suspense>
            <StripeLoadFallback />
          </RouteErrorBoundary>
        </LocalizationHandler>
      </LinkOutGate>
    </Router>
  );
};
